import React from 'react';
import { ArrowUpRight, ExternalLink, Facebook, Youtube, Radio, Sparkles } from 'lucide-react';
import { PLAYER_LINKS, PARTNER_TUNEIN, SOCIAL } from '@/constants/stations';

/**
 * ListenEverywhere — the user's requested home-page section that surfaces
 *   • the "Other Links" desktop player playlists (Winamp / WMP / RealPlayer / QuickTime / Web),
 *   • the TuneIn partner badge with deep link,
 *   • the Facebook + YouTube social CTAs.
 *
 * Placed after the donation banner — it continues to deliver value while keeping the page
 * deep with utility, so listeners feel taken care of (the same reciprocity engine that
 * funds the station).
 */

const PlayerChip = ({ link }) => (
  <a
    data-testid={`other-link-${link.id}`}
    href={link.href}
    target="_blank"
    rel="noopener noreferrer"
    download
    className="group relative rounded-2xl border border-amber/15 bg-espresso/60 px-5 py-4 flex items-center gap-4 hover:border-amber/50 hover:-translate-y-0.5 transition-all"
  >
    {/* Color disc */}
    <div
      className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center"
      style={{
        background: `radial-gradient(circle at 30% 30%, ${link.color}55, ${link.color}11 70%)`,
        boxShadow: `0 0 18px ${link.color}33 inset, 0 0 14px ${link.color}33`,
      }}
    >
      <Radio className="w-4 h-4" style={{ color: link.color }} />
    </div>
    <div className="min-w-0 flex-1">
      <div className="font-display text-base text-cream leading-tight truncate">{link.label}</div>
      <div className="text-xs text-cream/55 font-body truncate">{link.blurb}</div>
    </div>
    <ArrowUpRight className="w-4 h-4 text-cream/40 group-hover:text-amber transition-colors shrink-0" />
  </a>
);

const ListenEverywhere = () => (
  <section data-testid="listen-everywhere" className="relative py-20 md:py-24">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="text-center mb-12">
        <div className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold mb-3">
          Listen everywhere
        </div>
        <h2 className="font-display text-4xl md:text-5xl text-cream leading-tight">
          One station, <span className="font-serif-italic italic text-amber">a thousand doors.</span>
        </h2>
        <p className="mt-5 text-cream/65 font-body text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
          Pick the player you love — desktop classics, our partner directory or social — and follow
          the music wherever you go.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        {/* OTHER LINKS – grid of playlist file shortcuts */}
        <div className="lg:col-span-2 rounded-3xl border border-amber/15 bg-vinyl/40 p-6 md:p-7">
          <div className="flex items-center gap-2 mb-5">
            <Sparkles className="w-4 h-4 text-amber" />
            <div className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold">
              Other links
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PLAYER_LINKS.map((l) => (
              <PlayerChip key={l.id} link={l} />
            ))}
          </div>
          <p className="mt-5 text-xs text-cream/45 font-body italic">
            Each link downloads a playlist file — open it with your installed player (Winamp,
            Windows Media Player, RealPlayer, QuickTime). Tip: right-click → &quot;Open with&quot;.
          </p>
        </div>

        {/* TuneIn partner + Socials */}
        <div className="lg:col-span-1 flex flex-col gap-6">
          {/* TuneIn partner card */}
          <a
            data-testid="tunein-partner-link"
            href={PARTNER_TUNEIN}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative rounded-3xl border border-amber/20 overflow-hidden hover:-translate-y-0.5 transition-transform"
            style={{
              background: 'linear-gradient(135deg, #0F2147 0%, #14B8A6 100%)',
            }}
          >
            <div className="absolute inset-0 opacity-30"
              style={{ background: 'radial-gradient(closest-side at 20% 10%, rgba(255,255,255,0.4), transparent)' }} />
            <div className="relative p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-full bg-white/15 backdrop-blur flex items-center justify-center border border-white/30">
                  {/* TuneIn-style mark */}
                  <span className="font-display text-white text-lg leading-none">t</span>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-white/80 font-bold">Partner</div>
                  <div className="font-display text-xl text-white leading-tight">TuneIn</div>
                </div>
              </div>
              <div className="font-display text-2xl text-white leading-tight">
                Listen on TuneIn.
              </div>
              <div className="mt-1.5 text-sm text-white/75 font-body">
                Featured on the world&apos;s biggest radio directory.
              </div>
              <div className="mt-4 inline-flex items-center gap-2 text-white/90 group-hover:text-white text-xs uppercase tracking-[0.18em] font-body font-bold">
                Open TuneIn <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
            </div>
          </a>

          {/* Socials */}
          <div className="rounded-3xl border border-amber/15 bg-espresso/60 p-6">
            <div className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold mb-4">
              Follow the station
            </div>
            <div className="flex gap-3">
              <a
                data-testid="home-facebook-link"
                href={SOCIAL.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex-1 rounded-2xl border border-amber/15 hover:border-[#1877F2] bg-vinyl/60 p-4 flex flex-col items-center gap-2 hover:-translate-y-0.5 transition-all"
                aria-label="Facebook"
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg,#1877F2,#0a4ea6)' }}>
                  <Facebook className="w-5 h-5 text-white" fill="currentColor" />
                </div>
                <span className="text-[10px] tracking-[0.2em] uppercase text-cream/70 group-hover:text-cream font-bold font-body">
                  Facebook
                </span>
              </a>
              <a
                data-testid="home-youtube-link"
                href={SOCIAL.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex-1 rounded-2xl border border-amber/15 hover:border-[#FF0033] bg-vinyl/60 p-4 flex flex-col items-center gap-2 hover:-translate-y-0.5 transition-all"
                aria-label="YouTube"
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg,#FF0033,#8b0019)' }}>
                  <Youtube className="w-5 h-5 text-white" fill="currentColor" />
                </div>
                <span className="text-[10px] tracking-[0.2em] uppercase text-cream/70 group-hover:text-cream font-bold font-body">
                  YouTube
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default ListenEverywhere;
